import { captureException } from "@sentry/nextjs";
import { Col, Divider, Input, Row, Spin, Typography } from "antd";
import dynamic from "next/dynamic";
import Link from "next/link";
import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useCookies } from "react-cookie";
import { useIntl, defineMessages, FormattedMessage } from "react-intl";

import { rtfValues } from "../../../lib/formats/common";
import { SignUpMutation, UserError } from "../../../lib/graphql";
import { useCurrentLocale } from "../../../lib/hooks/basic/locales";
import { useCurrentUser, useSignUpUser } from "../../../lib/hooks/basic/users";
import {
  captureSignupEvent,
  UTM_COOKIE_NAMES,
} from "../../../lib/posthog/acquisition";
import { hardRedirect } from "../../../lib/redirect";
import routes from "../../../lib/routes";
import {
  Form,
  type FormProps,
  RuleBuilder,
  Rules,
  useFormContext,
} from "../../form/Form";
import { SubmitButton } from "../../form/SubmitButton";
import { Space } from "../../shared/Space";
import { useTurnstile } from "./useTurnstile";

const PhoneInput = dynamic(() => import("antd-phone-input"), {
  ssr: false,
  loading: () => <Spin style={{ marginBottom: 24 }} />,
});

const messages = defineMessages({
  accountTitle: {
    id: "user.signUp.completeInfo",
    defaultMessage: "Completa la siguiente información",
  },
  accountSubtitle: {
    id: "user.signUp.subtitle",
    defaultMessage:
      "You’re one step away from digitizing your farm. Create your <strong>{planName}</strong> account and start logging your activities in minutes.",
  },
  farmTitle: {
    id: "user.signUp.farmTitle",
    defaultMessage: "Now, tell us about your farm.",
  },
  farmSubtitle: {
    id: "user.signUp.farmSubtitle",
    defaultMessage:
      "We’ll use this info to set up your first farm in Aragro. It helps us personalize your experience from the start. It's your machete after all!",
  },
  submitFree: {
    id: "user.signUp.submitFree",
    defaultMessage: "Sign up for free!",
  },
  submitPaid: {
    id: "user.signUp.submitPaid",
    defaultMessage: "Start now!",
  },
  creating: {
    id: "user.signUp.creating",
    defaultMessage: "Creating your account...",
  },
  firstName: { id: "user.yourFirstName", defaultMessage: "Your First Name" },
  lastName: { id: "user.yourLastName", defaultMessage: "Your Last Name" },
  email: { id: "users.yourEmail", defaultMessage: "Your email" },
  password: { id: "user.yourPassword", defaultMessage: "Create your password" },
  passwordMinLength: {
    id: "user.passwordMinLength",
    defaultMessage: "Password must be at least 8 characters",
  },
  passwordConfirmation: {
    id: "user.passwordConfirmation",
    defaultMessage: "Password Confirmation",
  },
  passwordConfirmationError: {
    id: "form.validation.passwordConfirmation.match",
    defaultMessage: "Passwords don't match. Please review",
  },
  farmName: {
    id: "user.farmNamePlaceholder",
    defaultMessage:
      "What's your farm's name? Ex. Hacienda Buenos Aires, San Antonio Farm",
  },
  country: {
    id: "user.countryPlaceholder",
    defaultMessage: "Where is your farm located?",
  },
  farmSize: {
    id: "user.farmSizePlaceholder",
    defaultMessage: "What is the size of your farm?",
  },
  phoneNumber: {
    id: "user.phoneNumberPlaceholder",
    defaultMessage: "Your mobile number",
  },
  phoneNumberCountry: {
    id: "user.phoneNumberCountryPlaceholder",
    defaultMessage: "Search country",
  },
  captchaError: {
    id: "form.validation.captcha",
    defaultMessage: "Please complete the captcha",
  },
});

export type OnSaveProp<T> = (props: {
  variables: T;
  setCreating: (creating: boolean) => void;
  setSubmitting: (submitting: boolean) => void;
  showErrors: (errors: UserError[]) => void;
  showSuccess: (message: string) => void;
}) => Promise<SignUpMutation | undefined>;

export type NewAccountFormProps = {
  token?: string | null;
  plan?: string | null;
  period?: string | null;
  tenantName?: string | null;
  countryCode?: string | null;
  onSuccess?: (result: SignUpMutation["signUp"]) => void;
};

export default function ShortSignupForm({
  token,
  plan,
  period,
  tenantName,
  countryCode,
  onSuccess,
}: NewAccountFormProps) {
  const intl = useIntl();
  const { user } = useCurrentUser();
  const [creating, setCreating] = useState(!!user);
  const { currentLocale } = useCurrentLocale();
  const localeCode = currentLocale || "es";
  const showContactInfo = !token;
  const [cookies] = useCookies(UTM_COOKIE_NAMES);
  const utmCookies = useRef(cookies);
  const hasCapturedView = useRef(false);

  // TODO: merge with pricing translations?
  const planName = intl.formatMessage({
    id: `subscriptionPlans.${plan || "free"}.name`,
  });

  const [save] = useSignUpUser();
  const {
    token: captchaToken,
    reset: resetCaptcha,
    containerRef: captchaContainerRef,
    ScriptTag: CaptchaScript,
  } = useTurnstile(localeCode);

  const initialValues = useMemo(
    () => ({
      email: "",
      countryCode,
      localeCode,
      password: "",
      passwordConfirmation: null,
      captchaResponse: "",
    }),
    [localeCode, countryCode]
  );

  const trackingContext = useMemo(
    () => ({
      form: "short" as const,
      plan,
      period,
      token,
      countryCode,
    }),
    [countryCode, period, plan, token]
  );

  useEffect(() => {
    if (hasCapturedView.current) return;
    hasCapturedView.current = true;
    captureSignupEvent("signup_viewed", trackingContext, utmCookies.current);
  }, [trackingContext]);

  const onSubmit = useCallback<
    NonNullable<FormProps<typeof initialValues>["onSubmit"]>
  >(
    async (variables, { setSubmitting, showErrors }) => {
      setSubmitting(true);
      const submissionContext = {
        ...trackingContext,
        countryCode: variables.countryCode || countryCode,
      };
      captureSignupEvent("signup_submitted", submissionContext, cookies);

      const showCaptchaError = () => {
        showErrors([
          {
            path: "captchaResponse",
            message: intl.formatMessage(messages.captchaError),
          },
        ]);
      };

      if (!captchaToken) {
        captureSignupEvent("signup_failed", submissionContext, cookies, {
          reason: "captcha_missing",
        });
        resetCaptcha();
        showCaptchaError();
        setSubmitting(false);
        return;
      }

      if (typeof window === "undefined" || !window.turnstile) {
        captureSignupEvent("signup_failed", submissionContext, cookies, {
          reason: "captcha_unavailable",
        });
        captureException(new Error("Captcha not available on signup form"));
        showCaptchaError();
        resetCaptcha();
        setSubmitting(false);
        return;
      }

      variables.captchaResponse = captchaToken;

      if (
        variables.passwordConfirmation &&
        variables.password !== variables.passwordConfirmation
      ) {
        captureSignupEvent("signup_failed", submissionContext, cookies, {
          reason: "password_confirmation_mismatch",
        });
        showErrors([
          {
            path: "passwordConfirmation",
            message: intl.formatMessage(messages.passwordConfirmationError),
          },
        ]);
        setSubmitting(false);
        return;
      }

      try {
        const result = await save({
          variables: {
            ...variables,
            invitationToken: token,
            plan,
            period,
            tenant: tenantName,
          },
        });

        if (!result || !result.data || !result.data.signUp) {
          captureSignupEvent("signup_failed", submissionContext, cookies, {
            reason: "empty_response",
          });
          resetCaptcha();
          setSubmitting(false);
          return;
        }

        if (result.data.signUp.errors?.length) {
          captureSignupEvent("signup_failed", submissionContext, cookies, {
            reason: "server_errors",
          });
          showErrors(result.data.signUp.errors);
          resetCaptcha();
          setSubmitting(false);
          return;
        }

        captureSignupEvent("signup_succeeded", submissionContext, cookies);
        onSuccess?.(result.data.signUp);
        setCreating(true);
        localStorage.clear();

        hardRedirect(result.data.signUp.redirectTo || routes.welcome);
      } catch (error) {
        captureSignupEvent("signup_failed", submissionContext, cookies, {
          reason: "network_error",
        });
        captureException(error as Error);
        resetCaptcha();
        setSubmitting(false);
      }
    },
    [
      captchaToken,
      countryCode,
      cookies,
      intl,
      onSuccess,
      period,
      plan,
      resetCaptcha,
      save,
      tenantName,
      token,
      trackingContext,
    ]
  );

  return (
    <Space vertical size="large">
      <div>
        <Typography.Title level={3} className="auth-form-copy">
          {intl.formatMessage(messages.accountTitle)}
        </Typography.Title>

        <Typography.Text className="auth-form-copy" style={{ fontSize: 16 }}>
          {intl.formatMessage(messages.accountSubtitle, {
            planName: token ? "" : planName,
            ...rtfValues,
          })}
        </Typography.Text>
      </div>

      <Form initialValues={initialValues} onSubmit={onSubmit}>
        {creating ? (
          <Space vertical align="center">
            <Spin description={intl.formatMessage(messages.creating)} />
          </Space>
        ) : (
          <>
            <AccountStep showContactInfo={showContactInfo} />

            <div style={{ textAlign: "center" }}>
              {CaptchaScript}
              <Form.Item name="captchaResponse" noStyle>
                <div ref={captchaContainerRef} style={{ marginBottom: 16 }} />
              </Form.Item>
            </div>

            <Space vertical size="middle" align="center">
              <SubmitButton
                size="large"
                className="new-primary"
                style={{ minWidth: "180px" }}
                data-attr="signup/submit-btn"
              >
                {token ? (
                  <FormattedMessage
                    id="user.acceptInvite.submit"
                    defaultMessage="Create account"
                  />
                ) : (
                  intl.formatMessage(
                    !plan || plan === "free"
                      ? messages.submitFree
                      : messages.submitPaid
                  )
                )}
              </SubmitButton>

              <AcceptTermsAndPolicyFormItem locale={localeCode} />
            </Space>

            <Divider style={{ margin: "30px 0" }}>
              <Typography.Text
                type="secondary"
                style={{
                  textTransform: "uppercase",
                  opacity: 0.7,
                  fontWeight: 400,
                }}
              >
                <FormattedMessage id="or" defaultMessage="Or" />
              </Typography.Text>
            </Divider>

            <div style={{ textAlign: "center", paddingBottom: 20 }}>
              <Link href={routes.user.login}>
                <Typography.Text type="secondary">
                  <FormattedMessage id="signIn" defaultMessage="Sign In" />
                </Typography.Text>
              </Link>
            </div>
          </>
        )}
      </Form>
    </Space>
  );
}

export function AccountStep({ showContactInfo }: { showContactInfo: boolean }) {
  const intl = useIntl();
  const { form } = useFormContext();

  return (
    <>
      {showContactInfo && (
        <>
          <Row gutter={16}>
            <Col span={24} md={12}>
              <Form.Item
                name="firstName"
                rules={[Rules.required, Rules.name, RuleBuilder.maxLength(100)]}
              >
                <Input
                  autoFocus
                  placeholder={intl.formatMessage(messages.firstName)}
                  className="new-input"
                  autoComplete="given-name"
                />
              </Form.Item>
            </Col>

            <Col span={24} md={12}>
              <Form.Item
                name="lastName"
                rules={[Rules.required, Rules.name, RuleBuilder.maxLength(100)]}
              >
                <Input
                  placeholder={intl.formatMessage(messages.lastName)}
                  className="new-input"
                  autoComplete="family-name"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="email"
            rules={[Rules.required, Rules.email]}
            validateTrigger="onBlur"
          >
            <Input
              placeholder={intl.formatMessage(messages.email)}
              className="new-input"
            />
          </Form.Item>
        </>
      )}

      <Form.Item
        name="password"
        rules={[
          Rules.required,
          { min: 8, message: intl.formatMessage(messages.passwordMinLength) },
        ]}
      >
        <Input.Password
          placeholder={intl.formatMessage(messages.password)}
          className="new-input"
        />
      </Form.Item>

      {!showContactInfo && (
        <Form.Item
          name="passwordConfirmation"
          rules={[
            Rules.required,
            { min: 8, message: intl.formatMessage(messages.passwordMinLength) },
          ]}
        >
          <Input.Password
            placeholder={intl.formatMessage(messages.passwordConfirmation)}
            className="new-input"
          />
        </Form.Item>
      )}

      {showContactInfo && (
        <div style={{ marginBottom: 24 }}>
          <Form.Item name="phoneNumber" rules={[Rules.required]}>
            <PhoneInput
              distinct
              enableSearch
              disableParentheses
              className="new-input"
              searchPlaceholder={intl.formatMessage(
                messages.phoneNumberCountry
              )}
              onChange={(value) => {
                if (!value.phoneNumber) {
                  form.setFieldsValue({
                    phoneNumber: null,
                  });
                  return;
                }
                form.setFieldsValue({
                  phoneNumber: `+${value.countryCode}${value.areaCode}${value.phoneNumber}`,
                });

                if (!form.getFieldValue("countryCode")) {
                  form.setFieldsValue({
                    countryCode: value.isoCode?.toUpperCase(),
                  });
                }
              }}
              preferredCountries={["gt", "ni", "cl", "co", "br"]}
              style={{ marginBottom: 8 }}
            />
          </Form.Item>
        </div>
      )}
    </>
  );
}

export function AcceptTermsAndPolicyFormItem({ locale }: { locale: string }) {
  return (
    <div style={{ textAlign: "center" }}>
      <FormattedMessage
        id="users.acceptTermsAndPolicies"
        defaultMessage="By signing up you accept our <terms>Terms and Conditions</terms> and <policies>Privacy Policy</policies>"
        values={{
          terms: (txt) => (
            <a
              target="_blank"
              rel="noreferrer"
              href={`https://aragro.com/${locale}/terms`}
            >
              {txt}
            </a>
          ),
          policies: (txt) => (
            <a
              target="_blank"
              rel="noreferrer"
              href={`https://aragro.com/${locale}/privacy`}
            >
              {txt}
            </a>
          ),
        }}
      />
    </div>
  );
}
